import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { RESULT } from "../constants/testIds";
import { Home, Send, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { QuestionReviewItem, BLANK_REASON_LABELS } from "../components/QuestionReviewItem";

const REASON_OPTIONS = Object.entries(BLANK_REASON_LABELS); // [value, label]

/**
 * Session result screen (route: /sonuc/:id).
 *
 * UX1.2:
 *  - Blank questions (no attempt row at completion time) are listed as "Boş".
 *  - Blank REASONS are collected here, in one batch, after completion —
 *    never during the session. The student may pick any subset and submit,
 *    or skip entirely.
 *  - Every question row expands into the SAME shared detail component used
 *    by the "Son Çalışmalarım" review screen (QuestionReviewItem) so the
 *    question body, choices, selected/correct highlight, explanation,
 *    solution and video render identically from both entry points.
 */
export default function SessionResult() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);
  const [reasons, setReasons] = useState({});      // question_id -> reason literal
  const [reasonsSaved, setReasonsSaved] = useState(false);
  const [reasonsDismissed, setReasonsDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: rv } = await api.get(`/sessions/${id}/review`);
      setData(rv);
      setReasonsSaved(!!rv.blank_reasons_saved);
    })();
  }, [id]);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500 bg-[#F8FAFC]">Sonuç hazırlanıyor…</div>;

  const items = data.items || [];
  const total = items.length || data.total_questions || 10;
  const correct = items.filter((x) => x.result === "CORRECT" || x.is_correct).length;
  const blankItems = items.filter((x) => x.result === "BLANK");
  const blank = blankItems.length;
  const wrong = items.length - correct - blank;
  const percent = total ? Math.round((correct / total) * 100) : 0;

  const showReasonPrompt = blank > 0 && !reasonsSaved && !reasonsDismissed;

  const submitReasons = async () => {
    if (!Object.keys(reasons).length) return;
    setSaving(true);
    try {
      const r = await api.post(`/sessions/${id}/blank-reasons`, { reasons });
      setReasonsSaved(true);
      toast.success(`${r.data?.saved ?? 0} neden kaydedildi. Teşekkürler!`);
    } catch {
      toast.error("Nedenler kaydedilemedi");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-16">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="rounded-2xl yz-hero-panel text-white p-8 relative overflow-hidden shadow-lg">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#FFC107]/20 blur-3xl" />
          <div className="absolute -left-16 -bottom-24 w-64 h-64 rounded-full bg-[#007BFF]/20 blur-3xl" />
          <div className="relative">
            <div className="text-white/70 text-sm uppercase tracking-widest font-mono">Test tamamlandı</div>
            <div data-testid={RESULT.score} className="font-display text-5xl mt-2">
              {correct} <span className="text-white/50">/ {total}</span>
            </div>
            <div className="mt-2 text-sm text-white/80">
              <span className="text-emerald-400">{correct} doğru</span>
              <span className="text-white/40"> · </span>
              <span className="text-rose-400">{wrong} yanlış</span>
              {blank > 0 && <>
                <span className="text-white/40"> · </span>
                <span data-testid="result-blank-count" className="text-slate-300">{blank} boş</span>
              </>}
            </div>
            <div className="mt-1 text-[#FFC107] font-mono text-sm">%{percent} doğru</div>
          </div>
        </div>

        {showReasonPrompt && (
          <div data-testid="blank-reasons-section"
               className="mt-6 rounded-2xl border border-[#FFE082] bg-[#FFF8E1] p-5 space-y-4">
            <div>
              <div className="font-display text-lg text-[#0B1A35]">{blank} soruyu boş bıraktın.</div>
              <div className="text-sm text-slate-600">Nedenini belirtmek ister misin? (İsteğe bağlı — öğrenme haritanı iyileştirmemize yardımcı olur.)</div>
            </div>
            <div className="space-y-3">
              {blankItems.map((it) => {
                const i = items.indexOf(it);
                return (
                  <div key={it.question_id} className="bg-white rounded-xl border border-[#FFE082] p-3">
                    <div className="text-xs font-mono text-slate-500 mb-2">Soru {i + 1} · {it.ref}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {REASON_OPTIONS.map(([value, label]) => {
                        const active = reasons[it.question_id] === value;
                        return (
                          <button
                            key={value}
                            data-testid={`blank-reason-${it.question_id}-${value}`}
                            onClick={() => setReasons((r) => ({ ...r, [it.question_id]: value }))}
                            className={`px-2.5 py-1.5 rounded-full text-xs border transition-colors ${
                              active
                                ? "border-[#007BFF] bg-[#007BFF] text-white"
                                : "border-[#E2E8F0] text-slate-600 hover:border-[#007BFF]"
                            }`}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button
                onClick={submitReasons}
                disabled={saving || Object.keys(reasons).length === 0}
                data-testid="blank-reasons-submit"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B1A35] text-white text-sm hover:bg-[#16294D] transition-colors disabled:opacity-50">
                <Send size={14}/> {saving ? "Kaydediliyor…" : "Gönder"}
              </button>
              <button
                onClick={() => setReasonsDismissed(true)}
                data-testid="blank-reasons-skip"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E2E8F0] bg-white text-sm text-slate-600">
                <SkipForward size={14}/> Geç
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 grid gap-2">
          {items.map((it, i) => (
            <QuestionReviewItem
              key={it.question_id}
              item={it}
              index={i}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? null : i)}
              tidPrefix="result-row"
            />
          ))}
        </div>

        <button onClick={() => nav("/")} data-testid={RESULT.backHome}
          className="yz-cta mt-8 w-full py-3 rounded-xl inline-flex items-center justify-center gap-2">
          <Home size={16}/> Panele dön
        </button>
      </div>
    </div>
  );
}
